import '@mobiscroll/react/dist/css/mobiscroll.min.css'
import { setOptions } from '@mobiscroll/react'
import { useState } from 'react'
import styled from 'styled-components'
import '../common/rotaStyles.css'
import { PageHeader, Tabs } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Home } from '@lmc/ts-common'
import { getNominative } from '@lmc/common/utils'
import { CalendarOptions, themeOptions } from '../common/constants'
import UserRota from './UserRota/UserRota'
import ResidentRota from './ResidentRota/ResidentRota'
import { RotaBuilderFiltersType } from 'screens/Rostering/types'
import { DAILY, WEEKLY } from 'screens/Rostering/constants'
import useCountChanges from 'screens/Rostering/hooks/useCountChanges'
import useRosterPublished from 'screens/Rostering/hooks/useRosterPublished'

setOptions(themeOptions)

const PublishedRota = () => {
  const { t } = useTranslation()

  const [selectedDate, setSelectedDate] = useState(new Date())

  const [timelineView, setTimelineView] = useState(CalendarOptions.weekly)

  const [filterParams, setFilterParams] = useState<RotaBuilderFiltersType>({})

  const onFilterChange = (value: RotaBuilderFiltersType) => {
    setFilterParams((prevFilters) => ({
      ...prevFilters,
      ...value,
    }))
  }

  const isDailyView =
    timelineView === CalendarOptions.daily ||
    timelineView === CalendarOptions.dailyZoomed

  const { countChangesData, refetchCountChanges } = useCountChanges({
    selectedDate,
    intervalType: isDailyView ? DAILY : WEEKLY,
    filterParams,
  })

  const {
    users,
    residents,
    usersEvents,
    residentsEvents,
    isPublishedRotaLoading,
    invalid,
    metadata,
  } = useRosterPublished({
    selectedDate,
    intervalType:
      timelineView === CalendarOptions.daily ||
      timelineView === CalendarOptions.dailyZoomed
        ? DAILY
        : WEEKLY,
    filterParams,
  })

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )
  const residentsLabel = `By ${getNominative(
    preferredPeopleNominative,
    'Resident'
  )}`

  return (
    <Container>
      <PageHeader title={t('PUBLISHED_ROTA')} />
      <Tabs
        tabBarStyle={tabBarStyle}
        items={[
          {
            key: '1',
            label: t('BY_TEAM_MEMBER'),
            children: (
              <UserRota
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                timelineView={timelineView}
                setTimelineView={setTimelineView}
                publishedRota
                invalid={invalid}
                resources={users}
                events={usersEvents}
                isLoading={isPublishedRotaLoading}
                filterParams={filterParams}
                onFilterChange={onFilterChange}
                isDailyView={isDailyView}
                refetchCountChanges={refetchCountChanges}
                countChangesData={countChangesData}
                metadata={metadata}
              />
            ),
          },
          {
            key: '2',
            label: residentsLabel,
            children: (
              <ResidentRota
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                timelineView={timelineView}
                setTimelineView={setTimelineView}
                publishedRota
                resources={residents}
                events={residentsEvents}
                isLoading={isPublishedRotaLoading}
                filterParams={filterParams}
                onFilterChange={onFilterChange}
                isDailyView={isDailyView}
                refetchCountChanges={refetchCountChanges}
                countChangesData={countChangesData}
                metadata={metadata}
              />
            ),
          },
        ]}
      />
    </Container>
  )
}

const tabBarStyle = { paddingLeft: 24 }

const Container = styled.div`
  padding: 24px 48px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export default PublishedRota
